import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context';
import { ArrowLeft, Upload, Check, Loader2, FileUp, Smartphone, AlertTriangle } from 'lucide-react';
import { cn, formatPhoneNumber } from '../lib/utils';
import { parseVCards, ParsedContact } from '../lib/vcardParser';
import ContactAvatar from './ContactAvatar';
import { Contact } from '../types';

interface ImportCandidate extends ParsedContact {
  selected: boolean;
  existingContact?: Contact; // set when a duplicate is found
}

// Contact Picker API type declarations
interface ContactPickerResult {
  name?: string[];
  tel?: string[];
  email?: string[];
}

interface ContactsManager {
  select(properties: string[], options?: { multiple?: boolean }): Promise<ContactPickerResult[]>;
  getProperties(): Promise<string[]>;
}

declare global {
  interface Navigator {
    contacts?: ContactsManager;
  }
}

function supportsContactPicker(): boolean {
  return 'contacts' in navigator && 'ContactsManager' in window;
}

/**
 * Find a matching existing contact by name (case-insensitive) or shared phone/email.
 */
function findDuplicate(candidate: ParsedContact, existingContacts: Contact[]): Contact | undefined {
  const candidateNameLower = candidate.name.toLowerCase().trim();
  const candidatePhoneDigits = new Set(
    candidate.phones.map((p) => p.replace(/\D/g, '')).filter((p) => p.length >= 7),
  );
  const candidateEmails = new Set(candidate.emails.map((e) => e.toLowerCase().trim()));

  for (const contact of existingContacts) {
    // Match by name
    if (contact.name.toLowerCase().trim() === candidateNameLower) {
      return contact;
    }

    // Match by phone or email
    if (contact.communicationMethods) {
      for (const method of contact.communicationMethods) {
        if (method.type === 'phone') {
          const digits = method.value.replace(/\D/g, '');
          if (digits.length >= 7 && candidatePhoneDigits.has(digits)) {
            return contact;
          }
        }
        if (method.type === 'email' && candidateEmails.has(method.value.toLowerCase().trim())) {
          return contact;
        }
      }
    }
  }

  return undefined;
}

export default function ImportContacts() {
  const navigate = useNavigate();
  const { state, addContact, updateContact } = useApp();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [candidates, setCandidates] = useState<ImportCandidate[]>([]);
  const [importing, setImporting] = useState(false);
  const [importedCount, setImportedCount] = useState(0);
  const [mergedCount, setMergedCount] = useState(0);
  const [step, setStep] = useState<'choose' | 'review' | 'done'>('choose');

  // Tag candidates with duplicates on state change
  const tagWithDuplicates = (parsed: ParsedContact[]): ImportCandidate[] => {
    return parsed.map((c) => {
      const existing = findDuplicate(c, state.contacts);
      return {
        ...c,
        selected: !existing, // deselect duplicates by default
        existingContact: existing,
      };
    });
  };

  // Counts
  const selectedCount = candidates.filter((c) => c.selected).length;
  const duplicateCount = candidates.filter((c) => c.existingContact).length;
  const selectedDuplicates = candidates.filter((c) => c.selected && c.existingContact).length;
  const selectedNew = candidates.filter((c) => c.selected && !c.existingContact).length;

  // ─── Contact Picker API (Android Chrome) ────────────────────────────────
  const handleContactPicker = async () => {
    if (!navigator.contacts) return;

    try {
      const supported = await navigator.contacts.getProperties();
      const props = ['name'];
      if (supported.includes('tel')) props.push('tel');
      if (supported.includes('email')) props.push('email');

      const results = await navigator.contacts.select(props, { multiple: true });

      const parsed: ParsedContact[] = results
        .filter((r) => r.name?.[0])
        .map((r) => ({
          name: r.name![0],
          phones: r.tel || [],
          emails: r.email || [],
          socialProfiles: [],
        }));

      if (parsed.length > 0) {
        setCandidates(tagWithDuplicates(parsed));
        setStep('review');
      }
    } catch (err) {
      console.warn('[import] Contact Picker error:', err);
    }
  };

  // ─── vCard file import ──────────────────────────────────────────────────
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const parsed = parseVCards(text);

      if (parsed.length > 0) {
        setCandidates(tagWithDuplicates(parsed));
        setStep('review');
      }
    } catch (err) {
      console.error('[import] vCard parse error:', err);
    }

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ─── Toggle selection ───────────────────────────────────────────────────
  const toggleCandidate = (idx: number) => {
    setCandidates((prev) => prev.map((c, i) => (i === idx ? { ...c, selected: !c.selected } : c)));
  };

  const toggleAll = () => {
    const allSelected = candidates.every((c) => c.selected);
    setCandidates((prev) => prev.map((c) => ({ ...c, selected: !allSelected })));
  };

  // ─── Import selected contacts ───────────────────────────────────────────
  const handleImport = async () => {
    setImporting(true);
    const selected = candidates.filter((c) => c.selected);
    let newCount = 0;
    let merged = 0;

    for (const candidate of selected) {
      const commMethods: { type: string; value: string }[] = [];

      candidate.phones.forEach((phone) => {
        commMethods.push({ type: 'phone', value: formatPhoneNumber(phone) });
      });
      candidate.emails.forEach((email) => {
        commMethods.push({ type: 'email', value: email });
      });
      candidate.socialProfiles.forEach((profile) => {
        if (profile.type !== 'other') {
          commMethods.push({ type: profile.type, value: profile.username });
        }
      });

      if (candidate.existingContact) {
        // Merge: add new communication methods that don't already exist
        const existing = candidate.existingContact;
        const existingMethods = existing.communicationMethods || [];
        const existingValues = new Set(
          existingMethods.map((m) => `${m.type}:${m.value.replace(/\D/g, '')}`),
        );

        const newMethods = commMethods.filter(
          (m) => !existingValues.has(`${m.type}:${m.value.replace(/\D/g, '')}`),
        );

        const updates: Record<string, unknown> = {};

        if (newMethods.length > 0) {
          updates.communicationMethods = [...existingMethods, ...newMethods];
        }

        // Add birthday if existing doesn't have one
        if (candidate.birthday && !existing.birthday) {
          updates.birthday = candidate.birthday;
        }

        // Add photo if existing doesn't have one
        if (candidate.photoDataUrl && !existing.photoUrl) {
          updates.photoUrl = candidate.photoDataUrl;
        }

        if (Object.keys(updates).length > 0) {
          updateContact(existing.id, updates);
        }

        merged++;
      } else {
        // New contact
        addContact({
          name: candidate.name,
          photoUrl: candidate.photoDataUrl || undefined,
          status: 'new' as const,
          healthScore: -1,
          lastInteractionDate: '',
          overdueDays: 0,
          tags: [],
          birthday: candidate.birthday || undefined,
          communicationMethods: commMethods.length > 0 ? commMethods : undefined,
          preferredCommMethod: commMethods.length > 0 ? commMethods[0].type : undefined,
          relationshipType: 'Friend',
          innerCircle: false,
        });
        newCount++;
      }
    }

    setImportedCount(newCount);
    setMergedCount(merged);
    setImporting(false);
    setStep('done');
  };

  return (
    <div className="relative flex h-dvh w-full flex-col bg-[#f8f5f5] overflow-x-hidden">
      {/* Header */}
      <div className="flex items-center bg-[#f8f5f5] p-4 pb-2 justify-between sticky top-0 z-10 shrink-0">
        <button
          onClick={() => navigate(-1)}
          className="text-slate-900 flex size-12 shrink-0 items-center justify-center -ml-2"
        >
          <ArrowLeft size={24} />
        </button>
        <h2 className="text-slate-900 text-lg font-bold leading-tight tracking-tight flex-1 text-center">
          Import Contacts
        </h2>
        <div className="size-12 shrink-0 -mr-2" />
      </div>

      {/* Step: Choose import method */}
      {step === 'choose' && (
        <div className="px-6 py-8 space-y-4 overflow-y-auto flex-1">
          <p className="text-slate-500 text-sm text-center mb-6">
            Import contacts from your phone or a contact file
          </p>

          {/* Contact Picker (Android) */}
          {supportsContactPicker() && (
            <button
              onClick={handleContactPicker}
              className="w-full flex items-center gap-4 p-5 bg-white border-2 border-slate-200 rounded-2xl hover:border-[#fa8585] transition-all active:scale-[0.98]"
            >
              <div className="size-12 rounded-full bg-[#fa8585]/10 flex items-center justify-center shrink-0">
                <Smartphone size={24} className="text-[#fa8585]" />
              </div>
              <div className="text-left">
                <p className="font-bold text-slate-900">From Phone Contacts</p>
                <p className="text-xs text-slate-400">Select contacts from your device</p>
              </div>
            </button>
          )}

          {/* vCard file import */}
          <button
            onClick={() => fileInputRef.current?.click()}
            className="w-full flex items-center gap-4 p-5 bg-white border-2 border-slate-200 rounded-2xl hover:border-[#fa8585] transition-all active:scale-[0.98]"
          >
            <div className="size-12 rounded-full bg-[#fa8585]/10 flex items-center justify-center shrink-0">
              <FileUp size={24} className="text-[#fa8585]" />
            </div>
            <div className="text-left">
              <p className="font-bold text-slate-900">From Contact File</p>
              <p className="text-xs text-slate-400">Import a .vcf (vCard) file</p>
            </div>
          </button>

          <input
            ref={fileInputRef}
            type="file"
            accept=".vcf,text/vcard,text/x-vcard"
            className="hidden"
            onChange={handleFileSelect}
          />

          {/* Help text */}
          <div className="pt-4 space-y-3">
            <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">
              How to export contacts
            </p>
            <div className="text-xs text-slate-400 space-y-2">
              <p>
                <span className="font-semibold text-slate-500">iPhone:</span> Open Contacts app
                &rarr; select contacts &rarr; Share &rarr; save as .vcf file
              </p>
              <p>
                <span className="font-semibold text-slate-500">Android:</span> Open Contacts app
                &rarr; Menu &rarr; Export &rarr; save as .vcf file
              </p>
              <p>
                <span className="font-semibold text-slate-500">Google Contacts:</span>{' '}
                contacts.google.com &rarr; Export &rarr; vCard format
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Step: Review & select */}
      {step === 'review' && (
        <>
          <div className="px-6 py-3 flex items-center justify-between border-b border-slate-200/50 shrink-0">
            <p className="text-sm text-slate-500">
              {selectedCount} of {candidates.length} selected
            </p>
            <button onClick={toggleAll} className="text-sm font-bold text-[#fa8585]">
              {candidates.every((c) => c.selected) ? 'Deselect All' : 'Select All'}
            </button>
          </div>

          {duplicateCount > 0 && (
            <div className="px-6 py-2 bg-amber-50 border-b border-amber-100 shrink-0">
              <p className="text-xs text-amber-600 font-medium">
                <AlertTriangle size={12} className="inline mr-1 -mt-0.5" />
                {duplicateCount} contact{duplicateCount !== 1 ? 's' : ''} already in your list —
                select to merge new info
              </p>
            </div>
          )}

          <div className="flex-1 overflow-y-auto px-6 py-3 space-y-2">
            {candidates.map((candidate, idx) => (
              <button
                key={idx}
                onClick={() => toggleCandidate(idx)}
                className={cn(
                  'w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left',
                  candidate.existingContact
                    ? candidate.selected
                      ? 'border-amber-400 bg-amber-50'
                      : 'border-amber-200 bg-amber-50/50'
                    : candidate.selected
                      ? 'border-[#fa8585] bg-[#fa8585]/5'
                      : 'border-slate-200 bg-white',
                )}
              >
                <div
                  className={cn(
                    'size-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors',
                    candidate.selected
                      ? candidate.existingContact
                        ? 'bg-amber-500 border-amber-500'
                        : 'bg-[#fa8585] border-[#fa8585]'
                      : 'border-slate-300 bg-white',
                  )}
                >
                  {candidate.selected && <Check size={14} className="text-white" />}
                </div>

                {/* Photo preview */}
                {candidate.photoDataUrl ? (
                  <div className="size-10 rounded-full overflow-hidden shrink-0 border border-slate-200">
                    <img src={candidate.photoDataUrl} alt="" className="size-full object-cover" />
                  </div>
                ) : (
                  <ContactAvatar name={candidate.name} size="sm" />
                )}

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-bold text-slate-900 truncate">{candidate.name}</p>
                    {candidate.existingContact && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-600 uppercase shrink-0">
                        Exists
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                    {candidate.phones.map((p, i) => (
                      <span key={`p-${i}`} className="text-xs text-slate-400 truncate">
                        {p}
                      </span>
                    ))}
                    {candidate.emails.map((e, i) => (
                      <span key={`e-${i}`} className="text-xs text-slate-400 truncate">
                        {e}
                      </span>
                    ))}
                    {candidate.socialProfiles
                      .filter((s) => s.type !== 'other')
                      .map((s, i) => (
                        <span key={`s-${i}`} className="text-xs text-slate-400 truncate capitalize">
                          {s.type}: {s.username}
                        </span>
                      ))}
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Sticky import button */}
          <div className="p-6 bg-[#f8f5f5] border-t border-slate-200/50 shrink-0 pb-[calc(1.5rem+env(safe-area-inset-bottom))]">
            <button
              onClick={handleImport}
              disabled={selectedCount === 0 || importing}
              className="w-full bg-[#fa8585] hover:bg-[#fa8585]/90 disabled:opacity-40 text-white font-bold py-4 rounded-xl shadow-lg shadow-[#fa8585]/30 flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
            >
              {importing ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  <span>Importing...</span>
                </>
              ) : (
                <>
                  <Upload size={20} />
                  <span>
                    {selectedDuplicates > 0 && selectedNew > 0
                      ? `Import ${selectedNew} · Merge ${selectedDuplicates}`
                      : selectedDuplicates > 0
                        ? `Merge ${selectedDuplicates} Contact${selectedDuplicates !== 1 ? 's' : ''}`
                        : `Import ${selectedNew} Contact${selectedNew !== 1 ? 's' : ''}`}
                  </span>
                </>
              )}
            </button>
          </div>
        </>
      )}

      {/* Step: Done */}
      {step === 'done' && (
        <div className="flex-1 flex flex-col items-center justify-center px-6 gap-6">
          <div className="size-20 rounded-full bg-green-100 flex items-center justify-center">
            <Check size={40} className="text-green-600" />
          </div>
          <div className="text-center">
            <h3 className="text-2xl font-bold text-slate-900 mb-2">All done!</h3>
            <p className="text-slate-500">
              {importedCount > 0 && (
                <>
                  {importedCount} contact{importedCount !== 1 ? 's' : ''} imported
                </>
              )}
              {importedCount > 0 && mergedCount > 0 && ', '}
              {mergedCount > 0 && <>{mergedCount} merged</>}
            </p>
          </div>
          <div className="flex gap-3 w-full max-w-xs">
            <button
              onClick={() => {
                setCandidates([]);
                setStep('choose');
              }}
              className="flex-1 py-3 px-4 rounded-xl font-bold bg-slate-100 text-slate-600 transition-colors active:scale-95"
            >
              Import More
            </button>
            <button
              onClick={() => navigate('/contacts')}
              className="flex-1 py-3 px-4 rounded-xl font-bold bg-[#fa8585] text-white shadow-lg shadow-[#fa8585]/20 transition-colors active:scale-95"
            >
              View Contacts
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
